import { Button, Hairline, Input, Message, Textarea } from "@components";
import { If } from "@components/control-statements";
import { Environment } from "@core/Environment";
import { useDebouncedCallback } from "@hooks";
import { FlowAPI } from "@providers/FlowAPI";
import styles from "@styles/forms-flow/creative.module.scss";
import { Utils } from "@utils/Utils";
import { useEffect, useState } from "react";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";

const urlTypes = {
  STANDARD: "STANDARD",
  CUSTOM: "CUSTOM",
};

export const SmsMessage = ({
  message,
  senderName,
  url,
  coupon,
  onChangeCreative,
  restrictions,
  nextStep,
}) => {
  const [holdingId, setHoldingId] = useState(null);

  const setMessage = (value) => {
    const filteredValue = value.replace(
      /[\u{1F000}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{2000}-\u{3300}\u{E000}-\u{F8FF}]+/gu,
      ""
    );
    onChangeCreative({
      ...(message || {}),
      template: filteredValue,
      type: "SMS",
    });
  };

  useEffect(() => {
    const hasLink = message?.template.includes("<link>");

    let template = message?.template;

    template = `${template || ""}${url?.url && !hasLink ? " <link>" : ""}`;

    setMessage(template);
  }, []);

  const getMountedTemplate = () => {
    return replaceTags(
      glueSender(senderName, message?.template || "", url, restrictions),
      coupon,
      senderName,
      url,
      restrictions
    );
  };

  const getTotalChars = () => {
    const final = getMountedTemplate();
    return `${final}`.length;
  };

  const getHoldingId = async () => {
    const holdingId = await EnvironmentStorage.getEnvironmentHoldingId();
    setHoldingId(holdingId);
  };

  useEffect(() => {
    getHoldingId();
  }, []);

  let MAX_CHARS_PER_SMS = 150;

  const isCiaTC = holdingId && holdingId === 811;
  const isCocoBambu = Environment.id === 49;

  if (isCocoBambu) MAX_CHARS_PER_SMS = 145;

  return (
    <>
      <p className={styles.label}>Escreva a mensagem que será enviada</p>
      <Textarea
        className="mt-20"
        floatLabel="Mensagem"
        design="two"
        maxLength={!isCiaTC ? 150 : null}
        onKeyDown={(e) => {
          if (!isCiaTC) {
            const totalChars = getTotalChars();

            const controlKeys = [
              "Backspace",
              "Delete",
              "ArrowLeft",
              "ArrowRight",
              "ArrowUp",
              "ArrowDown",
            ];

            if (totalChars >= MAX_CHARS_PER_SMS && !controlKeys.includes(e.key))
              e.preventDefault();
          }
        }}
        value={message?.template.replace(/<link>$/, "")}
        onChange={(e) => {
          const newValue = e.target.value;

          if (!newValue.includes("<link>") && !!url?.type && !!url?.url) {
            setMessage(newValue + "<link>");
          } else {
            setMessage(newValue);
          }
        }}
      />

      <If cond={getTotalChars() > MAX_CHARS_PER_SMS}>
        <Message
          type="warning"
          className="mt-10"
          text={`O limite de ${MAX_CHARS_PER_SMS} caracteres foi ultrapassado.`}
        />
      </If>

      <If cond={!isCiaTC}>
        <div className="d-flex justify-end">
          <p className={styles.smsCount}>
            <span className={styles.number}>{getTotalChars()}</span>
            <span className={styles.type}>/{MAX_CHARS_PER_SMS} caracteres</span>
          </p>
        </div>
      </If>

      <If cond={isCiaTC}>
        <SmsCounters
          message={message}
          coupon={coupon}
          sender={senderName}
          url={url}
          restrictions={restrictions}
          MAX_CHARS_PER_SMS={MAX_CHARS_PER_SMS}
        />
      </If>

      <p className={styles.label}>Pré-visualização</p>
      <pre
        className={styles.previewMessage}
        dangerouslySetInnerHTML={{
          __html:
            replaceGaps(
              glueSender(senderName, message?.template, url, restrictions),
              coupon,
              senderName,
              url,
              restrictions
            ) || ``,
        }}
      ></pre>

      <Hairline className="mt-30 mb-30" />
      <SmsTriggerShot
        message={message}
        coupon={coupon}
        sender={senderName}
        url={url}
      />

      <div className={styles.footer}>
        <Button
          className="w-100"
          design="primary"
          onClick={nextStep}
          disabled={getTotalChars() > MAX_CHARS_PER_SMS}
        >
          Próximo
        </Button>
      </div>
    </>
  );
};

const SmsTriggerShot = ({ url, message, coupon, sender, restrictions }) => {
  const [phones, setPhones] = useState("");
  const [remainTime, setRemainTime] = useState(0);
  const [isWaitingSending, setIsWaitingSending] = useState(false);

  const prefix = "+55";

  const sendSmsTest = async () => {
    const environmentId = Environment.id;

    if (!validatePhoneAndPrefix()) {
      return;
    }

    const phone = `${prefix}${phones}`.replace(/[^0-9]/g, "");

    let benefit = "";
    if (coupon && coupon.code) {
      benefit = `${coupon?.code}-A10`;
    }

    const hasLink = message?.template.includes("<link>");

    let template = message?.template;

    template = `${template || ""}${url?.url && !hasLink ? " <link>" : ""}`;

    setIsWaitingSending(true);
    const response = await FlowAPI.sendSmsTest(environmentId, {
      phone: phone,
      urlType: url?.type,
      template: template,
      replacements: {
        "<cupom>": benefit,
        "<link>":
          url?.type === urlTypes.STANDARD
            ? `${Environment.defaultShortUrl}`
            : `${url?.url}`,
        "<remetente>": sender
          ? sender.includes(":")
            ? `${sender}`
            : `${sender}:`
          : "",
      },
    });

    if (response.getCode(500) != 200) {
      const errorMessage = response.getErrors([
        "Ocorreu um erro no sistema!",
      ])[0];
      alert({ text: errorMessage });
      setIsWaitingSending(false);
      return;
    }

    const startTimer = () => {
      let startTime = 60;
      const intervalId = setInterval(() => {
        setRemainTime(startTime--);
        if (startTime == -1) {
          clearInterval(intervalId);
          setIsWaitingSending(false);
        }
      }, 1000);
    };
    startTimer();
  };

  const onSendSms = useDebouncedCallback(sendSmsTest, 1000);

  const validatePhoneAndPrefix = () => {
    const phoneValid = phones.replace(/\D/g, "").length === 11;
    const prefixValid = /^\+\d{2}$/.test(prefix);
    return phoneValid && prefixValid;
  };

  return (
    <>
      <p className={styles.label}>Disparo teste</p>
      <p className={styles.sublabel}>
        Informe o seu número de telefone para que possamos realizar um disparo
        teste.
      </p>
      <div className={styles.testMessage}>
        <div className="d-flex align-center">
          <Input
            placeholder="(00) 00000-0000"
            className="w-100"
            center
            value={phones}
            mask="(99) 99999-9999"
            onChange={(e) => setPhones(e.target.value)}
            disabled={isWaitingSending}
          />
        </div>

        <Button
          design="four"
          className={`mt-20 w-100`}
          disabled={
            isWaitingSending || (!isWaitingSending && !validatePhoneAndPrefix())
          }
          onClick={onSendSms}
        >
          Disparar
          <If cond={remainTime > 0}>
            <span>
              &nbsp;(
              {`${`${Math.floor(remainTime / 60)}`.padStart(2, "0")}:${`${
                remainTime % 60
              }`.padStart(2, "0")}`}
              )
            </span>
          </If>
        </Button>
      </div>
    </>
  );
};

const SmsCounters = ({
  message,
  coupon,
  sender,
  url,
  restrictions,
  MAX_CHARS_PER_SMS,
}) => {
  const getMountedTemplate = () => {
    return replaceTags(
      glueSender(sender, message?.template || "", url, restrictions),
      coupon,
      sender,
      url,
      restrictions
    );
  };

  const getTotalSMS = () => {
    const final = getMountedTemplate();
    return Math.ceil(`${final}`.length / MAX_CHARS_PER_SMS);
  };

  const getTotalChars = () => {
    const final = getMountedTemplate();
    return `${final}`.length;
  };

  return (
    <div className="d-flex justify-end">
      <p className={styles.anotherSmsCount}>
        <span className={styles.number}>{getTotalSMS()}</span>
        <span className={styles.type}>
          &nbsp;{getTotalSMS() > 1 ? "mensagens" : "mensagem"}
        </span>
        <span className={styles.number}>{getTotalChars()}</span>
        <span className={styles.type}>&nbsp;caracteres</span>
      </p>
    </div>
  );
};

const glueSender = (sender, template) => {
  return (sender ? "<remetente>" : "") + template;
};

const replaceTags = (template, coupon, senderName = "", url, restrictions) => {
  const message = template || "";

  let benefit = "";
  if (coupon && coupon.code) {
    benefit = `${coupon?.code}-A10`;
  }

  const tagPairs = [
    {
      from: /<cupom>/gi,
      to: `${benefit}`,
    },
  ];

  if (url?.url) {
    tagPairs.push({
      from: /<link>/gi,
      to:
        url?.type === urlTypes.STANDARD
          ? `${Environment.defaultShortUrl}?q=5599999999999`
          : `${Environment.defaultShortUrl}?q=5599999999999`,
    });
  }

  if (senderName) {
    tagPairs.push({
      from: /<remetente>/gi,
      to: senderName.includes(":")
        ? `${senderName} `
        : `${senderName}: `
        ? `${senderName} `
        : "",
    });
  }

  return tagPairs?.reduce(
    (str, tag) => str?.replace(tag.from, tag.to),
    Utils.removeAccents(String(message))
  );
};

const replaceGaps = (template, coupon, senderName = "", url, restrictions) => {
  const message = template || "";

  let benefit = "";
  if (coupon && coupon.code) {
    benefit = `${coupon?.code}-A10`;
  }

  const tagPairs = [
    {
      from: /<remetente>/gi,
      to: senderName
        ? senderName.includes(":")
          ? `<span style="color:#6161ff">${senderName} </span>`
          : `<span style="color:#6161ff">${senderName}: </span>`
        : "",
    },
    {
      from: /<cupom>/gi,
      to: `<span style="color:#6161ff">${benefit}</span>`,
    },
  ];

  if (url?.url) {
    tagPairs.push({
      from: /<link>/gi,
      to: `<span style="color:#6161ff">${
        url?.type === urlTypes.STANDARD
          ? `${Environment.defaultShortUrl}?q=5599999999999`
          : `${Environment.defaultShortUrl}?q=5599999999999`
      }</span>`,
    });
  }

  return tagPairs?.reduce(
    (str, tag) => str?.replace(tag.from, tag.to),
    Utils.removeAccents(String(message))
  );
};

export default SmsMessage;
